import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import clsx from 'clsx';

interface BoxMember {
  role: string;
}

interface Box {
  id: string;
  status: string;
  generation: number;
  slots_total?: number;
  current_slots?: number;
  member_count?: number;
  members?: BoxMember[];
  created_at: string;
}

export default function BoxListPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['boxes', search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await api.get(`/movement/boxes?${params}`);
      return data;
    },
  });

  const boxes: Box[] = data?.boxes ?? [];

  const statusConfig: Record<string, { badge: string; label: string; icon: string }> = {
    filling:   { badge: 'badge-yellow', label: 'In corso', icon: '🟡' },
    active:    { badge: 'badge-green',  label: 'Attivo',   icon: '🟢' },
    completed: { badge: 'badge-gray',   label: 'Completato', icon: '⚪' },
    closed:    { badge: 'badge-gray',   label: 'Chiuso',   icon: '🔒' },
  };

  const roleColor = (role: string) => {
    const map: Record<string, string> = {
      RECEIVER:  'bg-blue-400 dark:bg-blue-500',
      BRIDGE:    'bg-accent-400 dark:bg-accent-500',
      NEW_DONOR: 'bg-primary-400 dark:bg-primary-500',
    };
    return map[role?.toUpperCase()] ?? 'bg-surface-300 dark:bg-surface-600';
  };

  return (
    <>
      <Helmet><title>City-Box — React-City</title></Helmet>

      <div className="space-y-6 animate-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-heading">City-Box</h1>
            <p className="text-muted text-sm mt-1">
              Esplora i box disponibili o visualizza quelli in cui sei membro
            </p>
          </div>
          <Link to="/join" className="btn-primary no-underline">
            �️ Entra
          </Link>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="🔍 Cerca box per ID, generazione…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input max-w-xs !py-2"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input max-w-[160px] !py-2"
          >
            <option value="">Tutti gli stati</option>
            <option value="filling">In corso</option>
            <option value="active">Attivo</option>
            <option value="completed">Completato</option>
            <option value="closed">Chiuso</option>
          </select>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-blue-400 dark:bg-blue-500" />
            Receiver
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-accent-400 dark:bg-accent-500" />
            Bridge
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-primary-400 dark:bg-primary-500" />
            Nuovo Donatore
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-surface-300 dark:bg-surface-600 border border-dashed border-surface-400" />
            Slot vuoto
          </span>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-muted">Caricamento...</div>
        ) : boxes.length === 0 ? (
          <div className="card text-center py-12">
            <span className="text-4xl block mb-4">📦</span>
            <h3 className="text-lg font-semibold mb-2 text-heading">Nessun City-Box</h3>
            <p className="text-muted text-sm mb-4">
              Non ci sono ancora City-Box. Entra nel movimento per crearne uno!
            </p>
            <Link to="/join" className="btn-primary no-underline">�️ Inizia ora</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {boxes.map((box) => {
              const cfg = statusConfig[box.status] ?? statusConfig.filling;
              const slotsTotal = box.slots_total ?? 11;
              const slotsFilled = box.current_slots ?? box.member_count ?? 0;
              const members = box.members ?? [];
              const fillPct = Math.round((slotsFilled / slotsTotal) * 100);

              return (
                <Link
                  key={box.id}
                  to={`/boxes/${box.id}`}
                  className="card hover:border-primary-300 dark:hover:border-primary-500/40 cursor-pointer transition-all no-underline group"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-heading group-hover:text-primary-500 transition-colors">
                      {cfg.icon} Box #{box.id.slice(0, 6)}
                    </h3>
                    <span className={cfg.badge}>{cfg.label}</span>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-muted mb-1">
                      <span>{slotsFilled}/{slotsTotal} slot</span>
                      <span>{fillPct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-surface-100 dark:bg-surface-800 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-500 dark:bg-primary-400 transition-all duration-500"
                        style={{ width: `${fillPct}%` }}
                      />
                    </div>
                  </div>

                  {/* Slot preview (mini dots) */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {Array.from({ length: slotsTotal }).map((_, i) => {
                      const member = members[i];
                      return (
                        <div
                          key={i}
                          className={clsx(
                            'w-5 h-5 rounded-full transition-all',
                            member
                              ? roleColor(member.role)
                              : 'bg-surface-200 dark:bg-surface-700 border border-dashed border-surface-300 dark:border-surface-600'
                          )}
                          title={member ? member.role : `Slot ${i + 1} vuoto`}
                        />
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-between text-xs text-muted">
                    <span>Gen. {box.generation}</span>
                    <span>{new Date(box.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
